import { useEffect, useState } from 'react';
import EyeOutlined from '@ant-design/icons/lib/icons/EyeOutlined';
import LoadingOutlined from '@ant-design/icons/lib/icons/LoadingOutlined';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { OpEmpty } from 'new-components/DLS/OpEmpty/OpEmpty';
import { OpList } from 'new-components/DLS/OpList/OpList';
import { OpSpin } from 'new-components/DLS/OpSpin/OpSpin';
import { OpCol } from 'new-components/DLS/OpCol/OpCol';
import { OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { OpRow } from 'new-components/DLS/OpRow/OpRow';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { useSelectorJs } from 'utils/customHooks';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive } from 'utils/helpers';

import {
  DATE_TIME_AM_PM_FORMAT,
  DATE_TIME_FORMAT,
} from '../../../../constants/dates';
import { GuestPassState } from '../../../../store/slices/guestPassSlice';
import { VisitsState } from '../../../../store/slices/visitsSlice';
import { ImmutableRootState } from '../../../../store/store';
import { GuestPass, GuestPassLog } from '../../../../types/guestPassTypes';
import { toLocalByTimezone } from '../../../../utils/dates';
import { formatFullName } from '../../../../utils/utils';
import {
  getStatusColor,
  getStatusNameById,
} from '../../../../utils/visitorsHelper';

import './GuestPassLogTab.scss';

interface GuestPassByDateResponse {
  data?: GuestPass[] | null;
}

interface GuestPassLogResponse {
  data?: GuestPassLog[] | null;
}

interface EntryDetail {
  id: number;
  name: string;
}

interface EntryDetailsResponse {
  data?: EntryDetail[] | null;
}

const GuestPassLogTab = () => {
  const { t } = useTranslation();

  const { orgId } = getOpParams();

  const selectedVisit = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['visits', 'selectedVisit']),
  ) as VisitsState['selectedVisit'];
  const triggerUpdate = useSelectorJs(
    (state: ImmutableRootState): GuestPassState['triggerUpdate'] =>
      state.getIn([
        'guestPass',
        'triggerUpdate',
      ]) as GuestPassState['triggerUpdate'],
  );

  const tz = selectedVisit?.location?.timezone;

  const [guestPasses, setGuestPasses] = useState<GuestPass[]>([]);
  const [guestPassLogs, setGuestPassLogs] = useState<
    Record<number, GuestPassLog[]>
  >({});
  const [msiAltaEntries, setMsiAltaEntries] = useState<EntryDetail[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedLog, setSelectedLog] = useState<GuestPassLog | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const startDate = dayjs(selectedVisit?.scheduleStart || dayjs())
          .subtract(6, 'months')
          .format(DATE_TIME_FORMAT);
        const endDate = dayjs(selectedVisit?.scheduleEnd || dayjs())
          .add(6, 'months')
          .format(DATE_TIME_FORMAT);

        // Fetch guest passes
        const guestPassResponse = (await heliumReceive(
          'getMsiAltaGuestPassByDate',
          [orgId],
          {
            queryStringParams: {
              startDate,
              endDate,
              visitId: selectedVisit?.id,
            },
          },
        )) as GuestPassByDateResponse;
        const fetchedGuestPasses = Array.isArray(guestPassResponse.data)
          ? guestPassResponse.data
          : [];
        setGuestPasses(fetchedGuestPasses);

        // Fetch logs for each guest pass
        const logsByGuestPass: Record<number, GuestPassLog[]> = {};
        await Promise.all(
          fetchedGuestPasses.map(async (pass: GuestPass) => {
            const logResponse = (await heliumReceive('getMsiAltaGuestPassLog', [
              orgId,
              selectedVisit?.id,
              pass.id,
            ])) as GuestPassLogResponse;
            const logs = Array.isArray(logResponse.data)
              ? logResponse.data
              : [];
            logsByGuestPass[pass.id] = logs.sort(
              (a: GuestPassLog, b: GuestPassLog) =>
                dayjs(b.requestedUtc).diff(dayjs(a.requestedUtc)),
            );
          }),
        );
        setGuestPassLogs(logsByGuestPass);

        // Fetch entry names
        const entriesResponse = (await heliumReceive('getMsiAltaEntryDetails', [
          orgId,
        ])) as EntryDetailsResponse;
        setMsiAltaEntries(
          Array.isArray(entriesResponse.data) ? entriesResponse.data : [],
        );
      } catch {
        // Failed to fetch logs
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [
    orgId,
    selectedVisit?.id,
    triggerUpdate,
    selectedVisit?.scheduleEnd,
    selectedVisit?.scheduleStart,
  ]);

  const isGuestPassValid = (guestPass: GuestPass): boolean => {
    if (!guestPass?.startUtc || !guestPass?.endUtc) return false;
    const now = dayjs.utc();
    const start = dayjs.utc(guestPass.startUtc);
    const end = dayjs.utc(guestPass.endUtc);
    return now.isAfter(start) && now.isBefore(end) && guestPass.status === 1;
  };

  const getEntryName = (entryId: number): string => {
    const entry = msiAltaEntries.find((item) => item.id === entryId);
    return entry ? entry.name : t('visitors.guestPassLog.unknownEntry');
  };

  return (
    <>
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <OpSpin indicator={<LoadingOutlined spin />} />
        </div>
      ) : guestPasses.length === 0 ? ( // Check if guestPasses is empty
        <OpEmpty description={t('visitors.guestPassLog.noData')} />
      ) : (
        <OpSpace
          orientation="vertical"
          size="middle"
          style={{
            display: 'flex',
          }}
        >
          {guestPasses
            .sort((a, b) => {
              const validA = isGuestPassValid(a);
              const validB = isGuestPassValid(b);
              if (validA === validB) {
                return dayjs(b.startUtc).diff(dayjs(a.startUtc)); // Sort by start date if both valid/invalid
              }
              return validA ? -1 : 1; // Valid passes first
            })
            .map((guestPass) => (
              <div key={guestPass.id}>
                <OpList
                  header={
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span>
                          {t('visitors.guestPassLog.issued')} :{' '}
                          {toLocalByTimezone(guestPass.createdAt, tz)?.format(
                            DATE_TIME_AM_PM_FORMAT,
                          )}
                        </span>
                        <span
                          style={{
                            marginLeft: '16px',
                            fontWeight: 'bold',
                            color: isGuestPassValid(guestPass)
                              ? 'green'
                              : 'var(--colorTextDisabled)',
                          }}
                        >
                          {isGuestPassValid(guestPass)
                            ? t('visitors.guestPassLog.valid')
                            : t('visitors.guestPassLog.invalid')}
                        </span>
                      </div>
                      {guestPass.generatedBy?.[0]?.firstName ||
                      guestPass.generatedBy?.[0]?.lastName ? (
                        <div>
                          <span>{t('visitors.guestPassLog.issuedBy')} : </span>
                          {formatFullName(
                            guestPass.generatedBy?.[0].firstName,
                            null,
                            guestPass.generatedBy?.[0].lastName,
                          )}
                        </div>
                      ) : null}
                    </>
                  }
                  dataSource={guestPassLogs[guestPass.id] || []}
                  bordered
                  renderItem={(item) => {
                    const log = item as GuestPassLog;
                    return (
                      <OpList.Item
                        onClick={() => setSelectedLog(log)}
                        className="guest-pass-log-tab__list-item"
                      >
                        <div style={{ flex: 1 }}>
                          {toLocalByTimezone(log.requestedUtc, tz)?.format(
                            DATE_TIME_AM_PM_FORMAT,
                          )}
                        </div>
                        <div style={{ flex: 1 }}>
                          {getEntryName(log.entryId)}
                        </div>
                        <div
                          style={{
                            flex: 1,
                            color: log.success ? 'green' : 'var(--colorError)',
                          }}
                        >
                          {log.success
                            ? t('visitors.guestPassLog.success')
                            : t('visitors.guestPassLog.failed')}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            marginLeft: '16px',
                          }}
                        >
                          <EyeOutlined
                            style={{
                              fontSize: '18px',
                              color: 'var(--colorTextSecondary)',
                            }}
                          />
                        </div>
                      </OpList.Item>
                    );
                  }}
                />
              </div>
            ))}
        </OpSpace>
      )}
      {selectedLog && (
        <OpModal
          title={t('visitors.guestPassLog.title')}
          open={!!selectedLog}
          onCancel={() => setSelectedLog(null)}
          footer={null}
          width={720}
        >
          <OpForm
            initialValues={{
              visitor:
                formatFullName(
                  selectedVisit?.visitors[0]?.firstName || null,
                  selectedVisit?.visitors[0]?.middleName || null,
                  selectedVisit?.visitors[0]?.lastName || null,
                ) +
                ((selectedVisit?.visitors?.length || 0) > 1
                  ? ` (+${(selectedVisit?.visitors?.length || 0) - 1})`
                  : ''),
              status: getStatusNameById(
                selectedVisit?.visitStatus.id!,
                selectedVisit?.scheduleStart,
                selectedVisit?.location?.timezone,
              ),
              requested:
                toLocalByTimezone(selectedLog.requestedUtc, tz)?.format(
                  'YYYY-MM-DD h:mm A',
                ) || '',
              entry: getEntryName(selectedLog.entryId),
              ipAddress: selectedLog.ipAddress,
              result: selectedLog.success
                ? t('visitors.guestPassLog.success')
                : t('visitors.guestPassLog.failed'),
            }}
            defaultButtons={false}
            hasError={false}
            onSubmit={() => {}}
          >
            <OpRow gutter={16}>
              <OpCol span={12}>
                <OpForm.Input
                  label={t('visitors.guestPassLog.visitor')}
                  name="visitor"
                  readOnly
                />
              </OpCol>
              <OpCol span={12}>
                <OpForm.Input
                  label={t('visitors.guestPassLog.status')}
                  name="status"
                  readOnly
                  style={{
                    backgroundColor: getStatusColor(
                      getStatusNameById(
                        selectedVisit?.visitStatus.id!,
                        selectedVisit?.scheduleStart,
                        selectedVisit?.location?.timezone,
                      ),
                    ),
                    color: 'white',
                  }}
                />
              </OpCol>
            </OpRow>
            <OpRow gutter={16}>
              <OpCol span={12}>
                <OpForm.Input
                  label={t('visitors.guestPassLog.requested')}
                  name="requested"
                  readOnly
                />
              </OpCol>
              <OpCol span={12}>
                <OpForm.Input
                  label={t('visitors.guestPassLog.entry')}
                  name="entry"
                  readOnly
                />
              </OpCol>
            </OpRow>
            <OpRow gutter={16}>
              <OpCol span={12}>
                <OpForm.Input
                  label={t('visitors.guestPassLog.ipAddress')}
                  name="ipAddress"
                  readOnly
                />
              </OpCol>
              <OpCol span={12}>
                <OpForm.Input
                  label={t('visitors.guestPassLog.result')}
                  name="result"
                  readOnly
                  style={{
                    color: selectedLog.success ? 'green' : 'var(--colorError)',
                  }}
                />
              </OpCol>
            </OpRow>
          </OpForm>
        </OpModal>
      )}
    </>
  );
};

export default GuestPassLogTab;
